import type { Mutable } from 'utility-types'

import {
    Capability,
    ConnectwareError,
    ConnectwareErrorType,
    isAuthenticatingWithOtp,
    type MfaActivationData,
    type MfaUpdateCredentials,
    Translation,
    type ValidOtp,
} from '../../../domain'
import type { MfaService, TranslationService } from '../../../application'

import type { BackendJsonRequestContent, BackendJsonResponseContent } from '../../Connectware'
import { type ConnectwareHTTPServiceOptions, FetchConnectwareHTTPService } from '../Base'
import { mapBackupCodeBodyRequest, mapOtpBodyRequest } from './mappers'

export class ConnectwareHTTPMfaService extends FetchConnectwareHTTPService implements MfaService {
    private readonly errorHandlers = {
        500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, this.translationService.translate(Translation.SERVER_ERROR)),
    } as const

    constructor (options: ConnectwareHTTPServiceOptions, private readonly translationService: TranslationService) {
        super(options)
    }

    private mapInvalidCredentials (credentials: MfaUpdateCredentials): ConnectwareError {
        return new ConnectwareError(
            ConnectwareErrorType.AUTHENTICATION,
            this.translationService.translate(Translation.WRONG_MFA_CREDENTAILS, { type: isAuthenticatingWithOtp(credentials) ? 'otp' : 'backupCode' })
        )
    }

    isEnabled (): Promise<boolean> {
        return this.request({
            capability: Capability.USE_MFA,
            method: 'GET',
            path: '/api/mfa/isenrolled',
            authenticate: true,
            handlers: { 200: (response) => response.getJson<BackendJsonResponseContent<'/api/mfa/isenrolled'>>().then(({ isEnrolled }) => isEnrolled) },
        })
    }

    enable (): Promise<MfaActivationData> {
        return this.request({
            capability: Capability.USE_MFA,
            method: 'POST',
            path: '/api/mfa/enable',
            authenticate: true,
            handlers: {
                200: async (response) => {
                    const { uri } = await response.getJson<BackendJsonResponseContent<'/api/mfa/enable'>>()

                    const url = new URL(uri)
                    const secret = url.searchParams.get('secret')

                    if (!secret) {
                        throw new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Could not find secret in uri', { uri })
                    }

                    return { uri: url, secret }
                },
                409: () =>
                    new ConnectwareError(
                        ConnectwareErrorType.GENERAL_BUSINESS_RULE_INFRACTION,
                        this.translationService.translate(Translation.MFA_ALREADY_ENABLED_ERROR)
                    ),
                ...this.errorHandlers,
            },
        })
    }

    disable (credentials: MfaUpdateCredentials): Promise<void> {
        return this.request<'/api/mfa/disable', BackendJsonRequestContent<'/api/mfa/disable', 'post'>, void>({
            capability: Capability.USE_MFA,
            method: 'POST',
            path: '/api/mfa/disable',
            authenticate: true,
            body: isAuthenticatingWithOtp(credentials) ? mapOtpBodyRequest(credentials.otp) : mapBackupCodeBodyRequest(credentials.backupCode),
            handlers: { 401: () => this.mapInvalidCredentials(credentials), 200: () => Promise.resolve(), ...this.errorHandlers },
        })
    }

    confirmEnabling (otp: ValidOtp): Promise<string[]> {
        return this.request<'/api/mfa/validate', BackendJsonRequestContent<'/api/mfa/validate', 'post'>, string[]>({
            capability: Capability.USE_MFA,
            method: 'POST',
            path: '/api/mfa/validate',
            authenticate: true,
            body: mapOtpBodyRequest(otp),
            handlers: {
                ...this.errorHandlers,
                200: (response) =>
                    response.getJson<BackendJsonResponseContent<'/api/mfa/validate'>>().then(({ backupCodes }) => backupCodes as Mutable<typeof backupCodes>),
                401: () => this.mapInvalidCredentials({ otp, backupCode: null }),
            },
        })
    }

    regenerateBackupCodes (credentials: MfaUpdateCredentials): Promise<string[]> {
        return this.request<'/api/mfa/regenerate/backupcodes', BackendJsonRequestContent<'/api/mfa/regenerate/backupcodes', 'post'>, string[]>({
            capability: Capability.USE_MFA,
            method: 'POST',
            path: '/api/mfa/regenerate/backupcodes',
            authenticate: true,
            body: isAuthenticatingWithOtp(credentials) ? mapOtpBodyRequest(credentials.otp) : mapBackupCodeBodyRequest(credentials.backupCode),
            handlers: {
                ...this.errorHandlers,
                401: () => this.mapInvalidCredentials(credentials),
                200: (response) =>
                    response
                        .getJson<BackendJsonResponseContent<'/api/mfa/regenerate/backupcodes'>>()
                        .then(({ backupCodes }) => backupCodes as Mutable<typeof backupCodes>),
            },
        })
    }
}
